// src/components/ProblemSolutionSlides.js
import React, { useState } from "react";
import "./ProblemSolutionSlides.css";
import Learners from "./FluencyCoachCollaborator.png";
import Chart1 from "./NationalScaleProjection.png";
import Chart2 from "./5YearFinancialEcosystemGrowth.png";
import VoiceMirror from "./Impulse Meter.png";
import Dashboard from "./Admin Assessments.png";
import Globe from "./GlobalImpact.png";
import PlatformImg from "./Platform.png";
import PodcastImg from "./UnsplashImage.jpg";
import CommunityImg from "./LinkedInCommunity.png";
import TeamImg from "./iSW Banner 4.png";

const slides = [
  {
    id: 1,
    title: "The Real Problem Isn’t Grammar — It’s Speaking Anxiety.",
    subtitle: "Every minute, millions of adult learners avoid speaking English out of fear.",
    content: (
      <>
        <img
            src={Learners}
            alt="Overlay"
            style={{
            position: "relative",
            width: "55%",
            opacity: "0.80",
            }}
        />
        <ul>
          <li>1.5B+ people are learning English worldwide.</li>
          <li>95% never gain fluent confidence.</li>
          <li>Apps and classrooms teach <em>rules</em>, not <em>voice</em>.</li>
          <li>Real conversations trigger embarrassment and withdrawal.</li>
        </ul>
        <blockquote>
          “I freeze when I have to speak English.”
        </blockquote>
        <p className="tagline">The iSpeakWell Project turns anxiety into voice.</p>
      </>
    ),
  },
  {
    id: 2,
    title: "Fluency Made Visible.",
    subtitle: "Learners finally see, hear, and feel their progress.",
    content: (
      <>
        <img
          src={VoiceMirror}
          alt="Voice Mirror"
          style={{
            position: "relative",
            width: "55%",
            opacity: "0.85",
            borderRadius: "12px",
          }}
        />
        <ul>
          <li>Live visual feedback shows pitch, rhythm, and intensity.</li>
          <li>Phoneme transition maps reveal real-time articulation flow.</li>
          <li>Avatars mirror speech to connect sound, movement, and meaning.</li>
          <li>Fluency metrics transform anxiety into self-regulation.</li>
        </ul>
        <p>
          iSpeakWell transforms speech into data learners can understand — and confidence they can feel.
        </p>
      </>
    ),
  },
  {
    id: 3,
    title: "From Silence to Expression.",
    subtitle: "A guided path from private practice to real communication.",
    content: (
      <>
        <img
          src={Dashboard}
          alt="Fluency Dashboard"
          style={{
            position: "relative",
            width: "55%",
            opacity: "0.85",
            borderRadius: "12px",
          }}
        />
        <h3>Three Levels of Engagement</h3>
        <ol>
          <li><strong>Self-Training Tools</strong> — private reflection and voice control.</li>
          <li><strong>Speaking Rooms</strong> — live dialogues with learners and mentors.</li>
          <li><strong>Performance Opportunities</strong> — podcasts, public speaking, showcases.</li>
        </ol>
        <p>
          Each level deepens confidence and fluency — allowing learners to move at their own pace from imitation to impact.
        </p>
      </>
    ),
  },
  {
    id: 4,
    title: "From Studying English to Living It.",
    subtitle: "Confidence is the product. Tokens are the fuel.",
    content: (
      <>
        <img
            src={Chart1}
            alt="Overlay"
            style={{
            position: "relative",
            width: "55%",
            opacity: "0.80",
            }}
        />
        <h3>The iSpeakWell Ecosystem</h3>
        <ul>
          <li><strong>Token = $1.00</strong></li>
          <li><strong>Learners:</strong> Join live rooms (2 tokens/hour)</li>
          <li><strong>Teachers:</strong> Open rooms (10 tokens/session, earn 1 token per learner/hour)</li>
          <li><strong>Facilitators:</strong> 5 per teacher — neurodiverse assistants</li>
        </ul>
        <p>
          AI-guided speaking tasks, real-time conversation, and assessment feedback. <br />
          Inclusive hiring loop — every teacher supported by facilitators. <br />
          Users earn motivation and assessment credits through Growth, Serious, and Pro plans.
        </p>
      </>
    ),
  },
  {
    id: 5,
    title: "A Self-Funding, Socially Inclusive Global Model.",
    subtitle: "Fluency isn’t perfection — it’s freedom.",
    content: (
      <>
        <img
            src={Chart2}
            alt="Overlay"
            style={{
            position: "relative",
            width: "55%",
            opacity: "0.80",
            }}
        />
        <h3>5-Year Impact Snapshot</h3>
        <table className="impact-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Year 5</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Teachers</td><td>648</td></tr>
            <tr><td>Facilitators</td><td>3,240</td></tr>
            <tr><td>Learners Served</td><td>10.7M</td></tr>
            <tr><td>Ecosystem Value</td><td>$136M / year</td></tr>
            <tr><td>Platform Net Profit</td><td>$40M / year</td></tr>
          </tbody>
        </table>
        <p>
          By Year 5, iSpeakWell unites teachers, facilitators, and learners across universities worldwide —
          a $136 million self-sustaining fluency economy.
        </p>
      </>
    ),
  },
  {
    id: 6,
    title: "The iSpeakWell Project Platform",
    subtitle: "A digital training environment where learners, instructors, and AI tools build fluency together.",
    content: (
      <>
        <img
          src={PlatformImg}
          alt="iSpeakWell Platform"
          style={{
            position: "relative",
            width: "55%",
            opacity: "0.85",
            borderRadius: "12px",
          }}
        />
        <ul>
          <li>Users interact with other learners, instructors, and AI systems.</li>
          <li>Tools analyze pronunciation, rhythm, and fluency progress in real time.</li>
          <li>Feedback loops personalize learning and track measurable growth.</li>
          <li>Confidence and expressiveness become visible, trainable skills.</li>
        </ul>
        <p>
          The iSpeakWell Platform transforms silent practice into interactive growth —
          turning English learning into the art of fluent expression.
        </p>
      </>
    ),
  },
  {
    id: 7,
    title: "The iSpeakWell Podcast",
    subtitle: "Where language, culture, and communication meet in motion.",
    content: (
      <>
        <img
          src={PodcastImg}
          alt="iSpeakWell Podcast"
          style={{
            position: "relative",
            width: "55%",
            opacity: "0.85",
            borderRadius: "12px",
          }}
        />
        <ul>
          <li>Showcases real learner journeys and platform success stories.</li>
          <li>Explores topics vital to English fluency, confidence, and storytelling.</li>
          <li>Features first- and second-language speakers who use English professionally.</li>
          <li>Analyzes current events and presentation styles to model effective speech.</li>
          <li>Creates short-form highlights for TikTok, Instagram, and YouTube.</li>
        </ul>
        <p>
          The Podcast amplifies the mission — sharing insights and voices that make English
          fluency feel human, reachable, and inspiring.
        </p>
      </>
    ),
  },
  {
    id: 8,
    title: "The iSpeakWell Community",
    subtitle: "A professional meeting place for learners, teachers, and innovators.",
    content: (
      <>
        <img
          src={CommunityImg}
          alt="iSpeakWell Community"
          style={{
            position: "relative",
            width: "55%",
            opacity: "0.85",
            borderRadius: "12px",
          }}
        />
        <ul>
          <li>Hosts discussions on fluency, communication, and AI in education.</li>
          <li>Connects potential learners, instructors, and research partners.</li>
          <li>Shares new features, success stories, and podcast highlights.</li>
          <li>Promotes teaching and speaking opportunities within the platform.</li>
          <li>Builds bridges between language learning and professional identity.</li>
        </ul>
        <p>
          The iSpeakWell Community on LinkedIn transforms isolated learning into collective progress —
          a network where every conversation strengthens the movement.
        </p>
      </>
    ),
  },
  {
    id: 9,
    title: "The iSpeakWell Project Team",
    subtitle: "A creative alliance advancing fluency through education, technology, and media.",
    content: (
      <>
        <img
          src={TeamImg}
          alt="iSpeakWell Team"
          style={{
            position: "relative",
            width: "55%",
            opacity: "0.85",
            borderRadius: "12px",
          }}
        />
        <ul>
          <li>Designs, develops, and maintains innovative AI-driven learning tools.</li>
          <li>Trains users and instructors to maximize fluency outcomes.</li>
          <li>Assesses learner progress and refines measurement frameworks.</li>
          <li>Produces podcast and media content showcasing user voices.</li>
          <li>Engages directly with community members to guide platform growth.</li>
        </ul>
        <p>
          The iSpeakWell Team unites education, linguistics, and creative technology —
          shaping a global ecosystem where human expression is measurable and empowering.
        </p>
      </>
    ),
  },
  {
    id: 10,
    title: "The iSpeakWell Operational Model",
    subtitle: "An interconnected ecosystem of learning, media, and community.",
    content: (
      <>
        <style>
          {`
            /* Clockwise flow animation */
            @keyframes dashFlowCW {
              0% { stroke-dashoffset: 1000; }
              100% { stroke-dashoffset: 0; }
            }

            /* Node glow animation */
            @keyframes pulseGlow {
              0%, 100% { filter: drop-shadow(0 0 0px rgba(255,255,255,0)); }
              50% { filter: drop-shadow(0 0 25px rgba(255,255,255,0.9)); }
            }

            /* Sequential glows (clockwise: Platform → Community → Team → Podcast) */
            .node-platform  { animation: pulseGlow 8s ease-in-out infinite; animation-delay: 0s; }
            .node-community { animation: pulseGlow 8s ease-in-out infinite; animation-delay: 2s; }
            .node-team      { animation: pulseGlow 8s ease-in-out infinite; animation-delay: 4s; }
            .node-podcast   { animation: pulseGlow 8s ease-in-out infinite; animation-delay: 6s; }


            .flow-ring {
              animation: dashFlowCW 12s linear infinite;
            }
          `}
        </style>

        <svg
          viewBox="0 0 900 700"  // ⬅️ Increased height from 600 → 700
          style={{
            width: "90%",
            maxWidth: "900px",
            margin: "2rem auto",
            display: "block",
            overflow: "visible", // ⬅️ allows glow outside SVG bounds
          }}
        >
          {/* Animated flow ring */}
          <ellipse
            cx="450"
            cy="350" // ⬅️ Recentered vertically to match new height
            rx="300"
            ry="220"
            stroke="rgba(255,255,255,0.35)"
            strokeWidth="6"
            fill="none"
            strokeDasharray="20 15"
            className="flow-ring"
          />

          {/* Gradient ring */}
          <ellipse
            cx="450"
            cy="350"
            rx="300"
            ry="220"
            stroke="url(#grad)"
            strokeWidth="12"
            fill="none"
            opacity="0.6"
          />

          <defs>
            <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#4b9ce2">
                <animate
                  attributeName="stop-color"
                  values="#4b9ce2; #45c490; #f5a623; #e24b6b; #4b9ce2"
                  dur="10s"
                  repeatCount="indefinite"
                />
              </stop>
              <stop offset="100%" stopColor="#e24b6b">
                <animate
                  attributeName="stop-color"
                  values="#e24b6b; #4b9ce2; #45c490; #f5a623; #e24b6b"
                  dur="10s"
                  repeatCount="indefinite"
                />
              </stop>
            </linearGradient>
          </defs>

          {/* Core nodes with glow */}
          <circle cx="450" cy="140" r="90" fill="#4b9ce2" opacity="0.9" className="node-platform" />
          <circle cx="740" cy="350" r="90" fill="#f5a623" opacity="0.9" className="node-community" />
          <circle cx="450" cy="560" r="90" fill="#e24b6b" opacity="0.9" className="node-team" />
          <circle cx="160" cy="350" r="90" fill="#45c490" opacity="0.9" className="node-podcast" />
          
          {/* Labels */}
          <text x="450" y="145" textAnchor="middle" fill="white" fontSize="22" fontWeight="700">
            Platform
          </text>
          <text x="160" y="355" textAnchor="middle" fill="white" fontSize="22" fontWeight="700">
            Podcast
          </text>
          <text x="740" y="355" textAnchor="middle" fill="white" fontSize="22" fontWeight="700">
            Community
          </text>
          <text x="450" y="565" textAnchor="middle" fill="white" fontSize="22" fontWeight="700">
            Team
          </text>

          {/* Caption */}
          <text
            x="450"
            y="685"
            textAnchor="middle"
            fill="#bbb"
            fontSize="16"
            fontStyle="italic"
          >
            Energy flows clockwise through learning, media, and collaboration.
          </text>
        </svg>

        <p
          style={{
            textAlign: "center",
            marginTop: "1.5rem",
            opacity: "0.9",
            maxWidth: "700px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          The iSpeakWell ecosystem circulates clockwise — from the <strong>Platform</strong> to the{" "}
          <strong>Podcast</strong>, outward to the <strong>Community</strong>, and onward through the{" "}
          <strong>Team</strong> — sustaining continuous learning, visibility, and innovation.
        </p>
      </>
    ),
  },
  {
    id: 11,
    title: "A Global Movement for Human Fluency.",
    subtitle: "Turning learning into community, and community into change.",
    content: (
      <>
        <img
          src={Globe}
          alt="Global Impact Map"
          style={{
            position: "relative",
            width: "55%",
            opacity: "0.85",
            borderRadius: "12px",
          }}
        />
        <ul>
          <li>Universities, teachers, and learners connected across continents.</li>
          <li>Inclusive hiring and training for neurodiverse facilitators.</li>
          <li>Research partnerships measuring fluency, confidence, and social impact.</li>
          <li>Podcast and media outreach sharing learner success stories.</li>
        </ul>
        <p className="tagline">
          iSpeakWell isn’t just teaching English — it’s building a world where every learner finds their voice.
        </p>
      </>
    ),
  }
];

export default function ProblemSolutionSlides() {
  const [index, setIndex] = useState(0);

  const next = () => setIndex((i) => (i + 1) % slides.length);
  const prev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);

  const { title, subtitle, content } = slides[index];

  return (
    <div className="slides-container">
      <div className="slide-card">
        <h1 className="slide-title">{title}</h1>
        <h2 className="slide-subtitle">{subtitle}</h2>
        <div className="slide-content">{content}</div>
      </div>

      <div className="slide-controls">
        <button onClick={prev}>← Previous</button>
        <button onClick={next}>Next →</button>
      </div>

      <div className="slide-indicator">
        Slide {index + 1} of {slides.length}
      </div>
    </div>
  );
}
